import { useDispatch } from 'react-redux'
import { Dispatch } from 'redux'
import { useTranslation } from 'react-i18next'
import { History } from 'history'
import { BrowserRouter } from 'react-router-dom'
import { createModal, useModalize } from '@/components/modalize/mui'
import { toast } from '@/components/toast-notifications'
import { isTruthy } from '@/utils'
import { ApplicationMenuItems } from '@/features/Application/applicant-info/application-dropdown-menu'

interface Application {
  favourite: string
  id: string
  favicon: string
  listing_id: string
}

interface Props {
  application: Application
  history: History
  canCopyApplication: boolean
  showCopyApplicationModal: ({
    application,
    history,
    listingId
  }: {
    application: Application
    history: History
    listingId: string
  }) => void
  getContractButton: () => JSX.Element
  preferredName: string
}

interface ApplicationActions {
  favourite: (id: string) => void
  unfavourite: (id: string) => void
  type: (type: string) => void
}

const MobileMenuModal: React.FC<Props> = ({
  application,
  history,
  canCopyApplication,
  showCopyApplicationModal,
  getContractButton,
  preferredName
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<Dispatch<ApplicationActions>>()
  const isFavourite = application.favourite === '1'
  const { close } = useModalize()
  const listingId = application.listing_id
  const handleFavourite = async () => {
    if (isTruthy(isFavourite)) {
      const { response } = await dispatch.application.unfavourite(application.id)
      if (response.ok) {
        toast(
          t(
            'l.application.view.favourites_success_text.text',
            'To view your favourite applicants, please go to the applications page for this listing.'
          ),
          {
            title: `${preferredName} ${t(
              'l.application.view.remove_from_favourites_success.title',
              'removed from favourites'
            )}`,
            type: 'success'
          }
        )
        close()
      }
    } else {
      const { response } = await dispatch.application.favourite(application.id)
      if (response.ok) {
        toast(
          t(
            'l.application.view.favourites_success_text.text',
            'To view your favourite applicants, please go to the applications page for this listing.'
          ),
          {
            title: `${preferredName} ${t(
              'l.application.view.add_to_favourites_success.title',
              'added to favourites'
            )}`,
            type: 'success'
          }
        )
        close()
      }
    }
  }

  return (
    <BrowserRouter>
      <ApplicationMenuItems
        isFavourite={isFavourite}
        canCopyApplication={canCopyApplication}
        handleCopyApplicant={
          canCopyApplication
            ? () =>
                showCopyApplicationModal({
                  application,
                  history,
                  listingId
                })
            : null
        }
        handleAddFavourites={handleFavourite}
        t={t}
        handleClose={close}
        isDesktop={false}
        getContractButton={getContractButton}
        application={application}
        history={history}
      />
    </BrowserRouter>
  )
}

export const show = createModal(MobileMenuModal, {
  width: 'auto',
  maxWidth: '805px',
  sheet: true,
  hasHeader: false,
  customIsMobileBreakpoint: '900px'
})
